import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import { ButtonPrimary } from "@/components/Buttons";

const STANDARDS = [
    {
        number: "01",
        title: "Supplier Vetting",
        text: "Every supplier is assessed before their materials are considered. We review sourcing practices, quality systems and documentation standards — and we decline suppliers who cannot meet them.",
    },
    {
        number: "02",
        title: "Product Sourcing",
        text: "Materials are sourced deliberately, not opportunistically. We prioritise consistency of supply, controlled handling and complete origin documentation for everything we list.",
    },
    {
        number: "03",
        title: "Independent Third-Party Testing",
        text: "Verification is performed by independent laboratories with no stake in the outcome. Test results belong to the data, not to our marketing.",
    },
    {
        number: "04",
        title: "Purity Testing & COA Documentation",
        text: "Purity is confirmed analytically and recorded in a Certificate of Analysis for the relevant batch. Documentation is retained and made available, so claims can always be checked against evidence.",
    },
    {
        number: "05",
        title: "Batch & Lot Traceability",
        text: "Where applicable, materials are tracked by batch and lot number, connecting the material in your laboratory to the records behind it.",
    },
    {
        number: "06",
        title: "Quality Control & Transparency",
        text: "Defined release criteria, documented review, and a standing commitment to answer questions about any material we supply. Transparency is an operating standard, not a slogan.",
    },
];

export default function Standards() {
    return (
        <div data-testid="standards-page">
            <PageHeader
                eyebrow="Quality Framework"
                title="Our Standards"
                description="The standards behind every material Grayhaven supplies — from the suppliers we accept to the documentation we publish."
            />

            <section className="pb-24 sm:pb-32 px-6 lg:px-8">
                <div className="max-w-5xl mx-auto">
                    {STANDARDS.map((item, i) => (
                        <Reveal key={item.number} delay={Math.min(i * 0.05, 0.2)}>
                            <div
                                data-testid={`standard-item-${i}`}
                                className="group grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-4 sm:gap-10 py-10 border-b border-stone-200 hover:border-gold/30 transition-colors duration-300"
                            >
                                <span className="font-serif text-5xl sm:text-6xl font-light text-gold/40 group-hover:text-wine transition-colors duration-300">
                                    {item.number}
                                </span>
                                <div>
                                    <h2 className="font-serif text-2xl sm:text-3xl font-light text-brand">
                                        {item.title}
                                    </h2>
                                    <p className="mt-4 text-base leading-relaxed text-mist max-w-2xl">{item.text}</p>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="py-24 sm:py-28 px-6 lg:px-8 bg-white/70 border-t border-stone-200">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                    <Reveal>
                        <p className="eyebrow">Evidence On Request</p>
                        <h2 className="mt-6 font-serif text-3xl sm:text-4xl font-light tracking-tight text-brand">
                            Standards mean nothing without proof
                        </h2>
                        <p className="mt-6 text-base leading-relaxed text-mist">
                            Our testing and documentation practice is where these standards become visible. Learn how
                            independent verification works at Grayhaven, and how Certificates of Analysis are organised
                            for the materials we supply.
                        </p>
                        <div className="mt-10">
                            <ButtonPrimary to="/testing" data-testid="standards-testing-btn">
                                Testing & Documentation
                            </ButtonPrimary>
                        </div>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <div className="relative">
                            <div className="absolute -inset-3 border border-gold/25 rounded-sm" aria-hidden="true" />
                            <img
                                src="https://images.unsplash.com/photo-1617155093730-a8bf47be792d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxsYWJvcmF0b3J5JTIwZ2xhc3N3YXJlJTIwcmVzZWFyY2h8ZW58MHx8fHwxNzkxMzA3MDI2fDA&ixlib=rb-4.1.0&q=85"
                                alt="Laboratory glassware used in analytical testing"
                                className="relative w-full h-[380px] object-cover rounded-sm border border-stone-200 shadow-[0_16px_50px_rgba(44,58,78,0.06)]"
                            />
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
